/**
 * Library/Bibliotheek Constants and Utilities
 */

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// === UPLOAD CONFIGURATION ===
export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
export const MAX_FILE_SIZE_WITH_COMPRESSION_MB = 50;
export const MAX_FILE_SIZE_WITH_COMPRESSION = MAX_FILE_SIZE_WITH_COMPRESSION_MB * 1024 * 1024;

// Timeouts (ms)
export const UPLOAD_TIMEOUT_MS = 120000; // 2 min
export const COMPRESSION_TIMEOUT_MS = 300000; // 5 min
export const STALE_PROGRESS_TIMEOUT_MS = 15000; // 15 sec

// File type categories
export const COMPRESSIBLE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'svg', 'bmp', 'tiff'];
export const PREVIEWABLE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];

// File type icons
const FILE_ICONS = {
  pdf: '📕',
  doc: '📘', docx: '📘',
  xls: '📗', xlsx: '📗',
  ppt: '📙', pptx: '📙',
  txt: '📝',
  jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', webp: '🖼️',
  mp4: '🎬', mov: '🎬', avi: '🎬',
  mp3: '🎵', wav: '🎵',
  zip: '📦', rar: '📦',
};

/**
 * Get file type info for Cloudinary
 */
export const getFileTypeInfo = (extension) => {
  const ext = extension?.toLowerCase() || '';
  const isImage = IMAGE_EXTENSIONS.includes(ext);
  return { resourceType: isImage ? 'image' : 'raw', isImage };
};

/**
 * Get icon for file extension
 */
export const getFileIcon = (extension) => {
  return FILE_ICONS[extension?.toLowerCase()] || '📄';
};

/**
 * Check if file is previewable
 */
export const isPreviewable = (extension) => {
  return PREVIEWABLE_EXTENSIONS.includes(extension?.toLowerCase());
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Format date for display (Dutch locale)
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('nl-NL', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get auth headers for API calls
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

/**
 * Map API error to user-friendly Dutch message
 */
export const mapErrorMessage = (error, context = 'actie') => {
  if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
    return 'Actie geannuleerd';
  }
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return `Timeout - probeer opnieuw`;
  }
  if (!navigator.onLine) {
    return 'Geen internetverbinding';
  }
  if (error.response?.status === 401 || error.response?.status === 403) {
    return 'Sessie verlopen - log opnieuw in';
  }
  if (error.response?.status === 413) {
    return `Bestand te groot`;
  }
  if (error.response?.status === 404) {
    return 'Item niet gevonden';
  }
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.message) {
    return error.message;
  }
  return `Fout bij ${context}`;
};

/**
 * Build folder tree from flat list
 */
export const buildFolderTree = (folders, parentId = null) => {
  return folders
    .filter(f => f.parent_folder_id === parentId && !f.deleted_at)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(folder => ({
      ...folder,
      children: buildFolderTree(folders, folder.id)
    }));
};

/**
 * Get all available extensions from documents
 */
export const getUniqueExtensions = (documents) => {
  const extensions = new Set();
  documents.forEach(doc => {
    if (doc.extension) extensions.add(doc.extension.toLowerCase());
  });
  return Array.from(extensions).sort();
};
